// src/features/privateAlbum/pages/PrivateAlbumPage.jsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  IconButton,
  Container,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import usePrivateAlbum from "../hooks/usePrivateAlbum";
import AlbumManager from "../components/AlbumManager";
import AlbumList from "../components/AlbumList";
import AlbumForm from "../components/AlbumForm";
import PhotoUpload from "../components/PhotoUpload";
import ShareModal from "../components/ShareModal";
import AlbumStats from "../components/AlbumStats";
import Loader from "../../../components/common/Loader";
import Modal from "../../../components/common/Modal";
// Import Firebase Storage functions
import { storage } from "../../../config/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const PrivateAlbumPage = () => {
  const {
    albums,
    selectedAlbum,
    photos,
    loading,
    error,
    success,
    getAlbums,
    getAlbumById,
    createNewAlbum,
    updateExistingAlbum,
    deleteExistingAlbum,
    archiveExistingAlbum,
    restoreExistingAlbum,
    uploadNewPhotos,
    deleteExistingPhoto,
    updateExistingPhoto,
    setCover,
    shareWithClient,
    revokeAccessFromClient,
    getAlbumStats,
    stats,
    filters,
    updateFilters,
    clearErrorState,
    clearSuccessState,
  } = usePrivateAlbum();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedAlbumId, setSelectedAlbumId] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [loadingAction, setLoadingAction] = useState(false);

  // Fix: Reset filter to 'all' if it's set to 'archived' by default
  useEffect(() => {
    // Only reset if the filter is not already 'all' and the page just loaded
    if (filters.status === "archived" && !loading) {
      console.log("Resetting filter from archived to all");
      updateFilters({ status: "all" });
    }
  }, []); // Run only once on mount

  // Fetch albums when filters change
  useEffect(() => {
    getAlbums(filters);
  }, [filters, getAlbums]);

  const handleSelectAlbum = (albumId) => {
    setSelectedAlbumId(albumId);
    getAlbumById(albumId);
    getAlbumStats(albumId);
    setViewMode("details");
  };

  const handleBackToList = () => {
    setSelectedAlbumId(null);
    setViewMode("list");
  };

  const handleCreateAlbum = async (data) => {
    setLoadingAction(true);
    try {
      await createNewAlbum(data);
      setShowCreateModal(false);
      await getAlbums(filters);
      clearErrorState();
    } catch (error) {
      console.error("Failed to create album:", error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleUpdateAlbum = async (data) => {
    setLoadingAction(true);
    try {
      await updateExistingAlbum(selectedAlbumId, data);
      setShowEditModal(false);
      await getAlbumById(selectedAlbumId);
      clearErrorState();
    } catch (error) {
      console.error("Failed to update album:", error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDeleteAlbum = async (id) => {
    if (window.confirm("Are you sure you want to delete this album?")) {
      setLoadingAction(true);
      try {
        await deleteExistingAlbum(id);
        if (selectedAlbumId === id) {
          handleBackToList();
        }
        await getAlbums(filters);
        clearErrorState();
      } catch (error) {
        console.error("Failed to delete album:", error);
      } finally {
        setLoadingAction(false);
      }
    }
  };

  const handleArchiveAlbum = async (id) => {
    setLoadingAction(true);
    try {
      await archiveExistingAlbum(id);
      await getAlbumById(id);
      await getAlbums(filters);
      clearErrorState();
    } catch (error) {
      console.error("Failed to archive album:", error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleRestoreAlbum = async (id) => {
    setLoadingAction(true);
    try {
      await restoreExistingAlbum(id);
      await getAlbumById(id);
      await getAlbums(filters);
      clearErrorState();
    } catch (error) {
      console.error("Failed to restore album:", error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleUploadPhotos = async (files) => {
    if (!selectedAlbumId) {
      console.error("No album selected");
      return;
    }

    setLoadingAction(true);
    try {
      const uploadPromises = files.map(async (file) => {
        // Create a unique filename
        const timestamp = Date.now();
        const uniqueId = Math.random().toString(36).substring(2, 10);
        const fileName = `${timestamp}_${uniqueId}_${file.name}`;
        const storagePath = `albums/${selectedAlbumId}/photos/${fileName}`;
        const thumbnailPath = `albums/${selectedAlbumId}/thumbnails/${fileName}`;

        // Upload to Firebase Storage
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(storageRef);

        // For thumbnail, you can use the same URL or create a smaller version
        // For now, we'll use the same URL
        const thumbnailUrl = downloadUrl;

        return {
          fileName: file.name,
          fileSize: file.size,
          url: downloadUrl,
          thumbnailUrl: thumbnailUrl,
          storagePath: storagePath,
          thumbnailStoragePath: thumbnailPath,
          width: 0, // You can get this from image metadata
          height: 0,
          caption: "",
        };
      });

      const photosData = await Promise.all(uploadPromises);

      await uploadNewPhotos(selectedAlbumId, photosData, "current-user");
      await getAlbumById(selectedAlbumId);
      setShowUploadModal(false);
      clearErrorState();
    } catch (error) {
      console.error("Failed to upload photos:", error);
      // You can set an error state here to show to the user
    } finally {
      setLoadingAction(false);
    }
  };

  const handleShareAlbum = async (email, name, role) => {
    setLoadingAction(true);
    try {
      await shareWithClient(selectedAlbumId, email, name, role, "current-user");
      await getAlbumById(selectedAlbumId);
      setShowShareModal(false);
      clearErrorState();
    } catch (error) {
      console.error("Failed to share album:", error);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleRevokeAccess = async (accessId) => {
    if (window.confirm("Are you sure you want to revoke access?")) {
      setLoadingAction(true);
      try {
        await revokeAccessFromClient(accessId);
        await getAlbumById(selectedAlbumId);
        clearErrorState();
      } catch (error) {
        console.error("Failed to revoke access:", error);
      } finally {
        setLoadingAction(false);
      }
    }
  };

  if (loading && !albums.length) {
    return <Loader fullScreen />;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Private Albums
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your client photo albums
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setShowCreateModal(true)}
          startIcon={<AddIcon />}
          disabled={loadingAction}
        >
          New Album
        </Button>
      </Box>

      {/* Album List View */}
      {viewMode === "list" && (
        <AlbumList
          albums={albums}
          onSelect={handleSelectAlbum}
          onDelete={handleDeleteAlbum}
          onArchive={handleArchiveAlbum}
          onRestore={handleRestoreAlbum}
          filters={filters}
          onFilterChange={updateFilters}
          loading={loading}
        />
      )}

      {/* Album Detail View */}
      {viewMode === "details" && selectedAlbum && (
        <Box>
          <Button
            onClick={handleBackToList}
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 3 }}
          >
            Back to Albums
          </Button>

          <AlbumManager
            album={selectedAlbum}
            photos={photos}
            onEdit={() => setShowEditModal(true)}
            onDelete={() => handleDeleteAlbum(selectedAlbum.id)}
            onArchive={() => handleArchiveAlbum(selectedAlbum.id)}
            onRestore={() => handleRestoreAlbum(selectedAlbum.id)}
            onUpload={() => setShowUploadModal(true)}
            onShare={() => setShowShareModal(true)}
            onPhotoDelete={deleteExistingPhoto}
            onPhotoUpdate={updateExistingPhoto}
            onSetCover={setCover}
          />

          {stats && <AlbumStats stats={stats} />}
        </Box>
      )}

      {/* Create Album Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Album"
      >
        <AlbumForm
          onSubmit={handleCreateAlbum}
          onCancel={() => setShowCreateModal(false)}
          loading={loadingAction}
        />
      </Modal>

      {/* Edit Album Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Album"
      >
        <AlbumForm
          initialData={selectedAlbum}
          onSubmit={handleUpdateAlbum}
          onCancel={() => setShowEditModal(false)}
          loading={loadingAction}
        />
      </Modal>

      {/* Share Album Modal */}
      <Modal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title="Share Album"
      >
        <ShareModal
          albumId={selectedAlbumId}
          accessList={selectedAlbum?.accessList || []}
          onShare={handleShareAlbum}
          onRevoke={handleRevokeAccess}
          onCancel={() => setShowShareModal(false)}
          loading={loadingAction}
        />
      </Modal>

      {/* Upload Photos Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        title="Upload Photos"
        size="lg"
      >
        <PhotoUpload
          onUpload={handleUploadPhotos}
          onCancel={() => setShowUploadModal(false)}
          loading={loadingAction}
        />
      </Modal>
    </Container>
  );
};

export default PrivateAlbumPage;
